import { jwtDecode } from 'jwt-decode';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { Authorization, User, CompanyProfile, ServerJWTAuthorization, JWTTokenPayload, PricingPlanSubscription } from 'types';
import { usersApiSlice } from 'services/users';
import { PricingPlan } from 'types';

const tokenType : string = "Bearer"; 

export interface AuthState {
    authorization: Authorization | null;
    user: User | null;
    status: 'idle' | 'loading' | 'failed';
    realmId: number | null;
    selected_realm: CompanyProfile | null;
    active_subscription?: PricingPlanSubscription | null;
    language?: string;
}

const initialState: AuthState = {
    authorization: (localStorage.getItem('accessJWT') ? 
      ({ accessToken: localStorage.getItem('accessJWT'),
        refreshToken: localStorage.getItem('refreshJWT'),
        tokenType: tokenType,
      }) as Authorization : null),
    user: null,
    realmId: null,
    status: 'idle',
    selected_realm: null,
    active_subscription: null,
    language: localStorage.getItem('language') || 'en',
};

export const usersSlice = createSlice({
  name: 'users',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    tokensReceived: (state, action: PayloadAction<ServerJWTAuthorization>) => {
      state.authorization = ({
        accessToken: action.payload.access,
        refreshToken: action.payload.refresh,
        tokenType: "Bearer",
      }) as Authorization;
      localStorage.setItem('accessJWT', action.payload.access);
      localStorage.setItem('refreshJWT', action.payload.refresh);
      localStorage.setItem('tokenType', "Bearer");
      const decodedToken: JWTTokenPayload = jwtDecode(action.payload.access);
      state.realmId = decodedToken.realm_id || null;
    },
    setUserData: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    logout: (state) => {
      localStorage.removeItem('accessJWT');
      localStorage.removeItem('refreshJWT');
      localStorage.removeItem('tokenType');
      return {
        ...state,
        authorization: null,
        user: null,
        realmId: null,
      } as AuthState;
    },
    setLanguage: (state, action: PayloadAction<string>) => {
      state.language = action.payload;
      localStorage.setItem('language', action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(usersApiSlice.endpoints.loginJWT.matchFulfilled, (state, action) => {
        state.authorization = ({
          accessToken: action.payload.access,
          refreshToken: action.payload.refresh,
          tokenType: "Bearer",
        }) as Authorization;
        localStorage.setItem('accessJWT', action.payload.access);
        localStorage.setItem('refreshJWT', action.payload.refresh);
        localStorage.setItem('tokenType', "Bearer");
      })
      .addMatcher(usersApiSlice.endpoints.googleLogin.matchFulfilled, (state, action) => {
        state.authorization = ({
          accessToken: action.payload.access_token,
          expiresIn: action.payload.expires_in,
          refreshToken: action.payload.refresh_token,
          scope: action.payload.scope,
          tokenType: action.payload.token_type
        }) as Authorization;
        localStorage.setItem('accessToken', action.payload.access_token);
        localStorage.setItem('refreshToken', action.payload.refresh_token);
        localStorage.setItem('tokenType', action.payload.token_type);
      })
      .addMatcher(usersApiSlice.endpoints.getUserData.matchFulfilled, (state, action) => {
        state.user = ({
          id: action.payload.id,
          username: action.payload.username,
          email: action.payload.email,
          realm: action.payload.realm,
          user_profile: action.payload.user_profile,
          subscriptions: action.payload.subscriptions,
        }) as User;
        state.selected_realm = action.payload.realm || null;
        state.active_subscription = action.payload.subscriptions?.filter(sub => sub.is_active)[0] || null;
      })
      .addMatcher(usersApiSlice.endpoints.selectRealm.matchFulfilled, (state, action) => {
        state.authorization = ({
          accessToken: action.payload.access,
          refreshToken: action.payload.refresh,
          tokenType: "Bearer",
        }) as Authorization;
        localStorage.setItem('accessJWT', action.payload.access);
        localStorage.setItem('refreshJWT', action.payload.refresh);
        localStorage.setItem('tokenType', "Bearer");
        const decodedToken: JWTTokenPayload = jwtDecode(action.payload.access);
        state.realmId = decodedToken.realm_id;
      })
      .addMatcher(usersApiSlice.endpoints.logout.matchFulfilled, (state) => {
        state.authorization = null;
        state.user = null;
        localStorage.removeItem('accessJWT');
        localStorage.removeItem('refreshJWT');
        localStorage.removeItem('tokenType');
      })
  },
});

export const { tokensReceived, setUserData, logout, setLanguage } = usersSlice.actions;

export const selectAccessToken = (state: RootState) => state.users.authorization?.accessToken
export const selectUser = (state: RootState) => state.users.user;
export const selectRealmId = (state: RootState) => state.users.realmId;
export const selectSelectedRealm = (state: RootState) => state.users.selected_realm;
export const selectActiveSubscription = (state: RootState) => state.users.active_subscription;
export const selectActivePricingPlan = (state: RootState) => state.users.active_subscription?.pricing_plan as PricingPlan;
export const selectLanguage = (state: RootState) => state.users.language;

export default usersSlice.reducer;
